import type { Block, Blocks, QuestionBlock } from '@w/content-blocks'
import { getFractionalIndex } from '@w/content-blocks/src/utils/get-fractional-index'
import { getLexiconCardType, residueCells, type LexiconCardKind } from './lexicon-cards'
import { findBlock } from '@w/content-blocks/src/traverse'
import {
  entryParadigm,
  entrySenses,
  translationFor,
  type Example,
  type LexiconEntry,
  type RuleBlock,
  type Sense,
  type TranslationLanguage,
  type WordForm,
} from './lexicon-model'

export function readAloudBlock(
  entry: Extract<LexiconEntry, { kind: 'lexeme' }>,
  fractionalIndex: string,
  translationLanguage?: TranslationLanguage,
): QuestionBlock {
  const qid = entry.readAloudQuestionId
  return {
    id: qid,
    type: 'question',
    subtype: 'sr',
    qid,
    fractionalIndex,
    importance: 'low',
    difficulty: 'low',
    answer: { src: entry.audioSrc ?? '', transcript: entry.transcription },
    question: [
      _taskLabel(`${qid}-task`, 'SAY'),
      ..._imageBlock(qid, entry),
      _paragraph(
        `${qid}-cue`,
        translationFor(entry.translations, translationLanguage),
      ),
      _highlightedWord(`${qid}-word`, entry.headword),
    ],
  }
}

export function dictationBlock(
  entry: Extract<LexiconEntry, { kind: 'lexeme' }>,
  fractionalIndex: string,
  translationLanguage?: TranslationLanguage,
): QuestionBlock {
  const qid = entry.dictationQuestionId
  return {
    id: qid,
    type: 'question',
    subtype: 'sa',
    strict: true,
    qid,
    fractionalIndex,
    importance: 'low',
    difficulty: 'low',
    answer: [entry.headword],
    question: [
      _taskLabel(`${qid}-task`, 'SPELL'),
      ..._imageBlock(qid, entry),
      ...(entry.audioSrc
        ? [
            {
              id: `${qid}-audio`,
              type: 'audio' as const,
              src: entry.audioSrc,
              autoplay: true,
            },
          ]
        : []),
      _paragraph(
        `${qid}-cue`,
        translationFor(entry.translations, translationLanguage),
      ),
    ],
  }
}

export function senseRecallBlock(
  sense: Sense & { senseRecallQuestionId: string },
  fractionalIndex: string,
  translationLanguage?: TranslationLanguage,
): QuestionBlock {
  const qid = sense.senseRecallQuestionId
  // The word stays visible and highlighted inside its sentence (unni's
  // WordGradient): the learner supplies the meaning, not the form.
  const question: Blocks = [
    _taskLabel(`${qid}-task`, 'MEANING'),
    ..._senseImageBlock(qid, sense),
    _highlightedExample(qid, sense.example),
  ]
  if (sense.example.nodeAudioSrc) {
    question.push({
      id: `${qid}-audio`,
      type: 'audio',
      src: sense.example.nodeAudioSrc,
      autoplay: true,
    })
  }
  return {
    id: qid,
    type: 'question',
    subtype: 'la',
    qid,
    fractionalIndex,
    criteria: [
      translationFor(sense.translations, translationLanguage),
      sense.gloss,
    ],
    explanation: [
      _paragraph(`${qid}-translation`, translationFor(sense.translations, translationLanguage)),
      _paragraph(`${qid}-gloss`, sense.gloss),
    ],
    question,
  }
}

export function formRecallBlock(
  sense: Sense & { formRecallQuestionId: string },
  fractionalIndex: string,
  translationLanguage?: TranslationLanguage,
): Blocks {
  const qid = sense.formRecallQuestionId
  return [
    // The meaning is the PROMPT, not decoration. Without it the learner is
    // asked to produce a word from a picture alone — and form recall now leads
    // the sitting, so they may not have seen this word yet at all. The short
    // L1 translation comes first for the same reason the sense card leads with
    // it — see the sense-cards-lead-with-the-short-translation requirement.
    // No task label: the gap says "write here" and the meaning above says what
    // to write. "FILL" told the learner nothing they could not already see.
    clozeParagraph(
      qid,
      sense.example,
      fractionalIndex,
      sense.example.imageSrc,
      [translationFor(sense.translations, translationLanguage), sense.gloss],
      sense.example.nodeAudioSrc,
    ),
  ]
}

export function formRecallParadigmCellBlock(
  form: WordForm & { formRecallQuestionId: string },
  sense: Sense | undefined,
  fractionalIndex: string,
  headword = '',
  translationLanguage?: TranslationLanguage,
): Blocks {
  const qid = form.formRecallQuestionId
  return [
    // The cell, the lemma and the meaning all ride INSIDE the cloze paragraph:
    // the trainer renders only the block carrying the qid, so a sibling label
    // would be dropped. The cell IS the question — without it every case card
    // looks identical.
    // ...and the lemma says WHICH word's form to produce. The answer is an
    // inflected form, so showing the citation form gives nothing away.
    clozeParagraph(
      qid,
      form.example,
      fractionalIndex,
      form.example.imageSrc ?? sense?.example.imageSrc,
      [
        form.cell.toLocaleUpperCase(),
        // The lemma says WHICH word's form to produce — but for the citation
        // cell (nominative singular, infinitive) the lemma IS the answer, so
        // it is withheld there and the translation carries the prompt alone.
        headword === form.form ? '' : headword,
        sense ? translationFor(sense.translations, translationLanguage) : '',
      ],
      form.example.nodeAudioSrc,
      { importance: 'low', difficulty: 'low' },
    ),
  ]
}

// Trainer-only projection. Lexicon entry pages render EntryEditor, never these blocks.
export function entryTrainingBlocks(
  entry: LexiconEntry,
  rule?: RuleBlock,
  translationLanguage?: TranslationLanguage,
): Blocks {
  const indexes = _fractionalIndexes(entry)
  const result: Blocks = [
    _paragraph(
      `${entry.id}-intro`,
      `${entry.transcription} — ${translationFor(entry.translations, translationLanguage)}`,
    ),
  ]

  if (entry.kind === 'lexeme') {
    result.push(
      readAloudBlock(
        entry,
        indexes.get(entry.readAloudQuestionId) ?? '',
        translationLanguage,
      ),
      dictationBlock(
        entry,
        indexes.get(entry.dictationQuestionId) ?? '',
        translationLanguage,
      ),
    )
  }

  for (const sense of entrySenses(entry)) {
    result.push(
      _heading(
        `${sense.id}-h`,
        translationFor(sense.translations, translationLanguage),
      ),
      _paragraph(`${sense.id}-gloss`, sense.gloss),
    )
    if (sense.senseRecallQuestionId) {
      result.push(
        senseRecallBlock(
          { ...sense, senseRecallQuestionId: sense.senseRecallQuestionId },
          indexes.get(sense.senseRecallQuestionId) ?? '',
          translationLanguage,
        ),
      )
    }
    if (sense.formRecallQuestionId) {
      result.push(
        ...formRecallBlock(
          { ...sense, formRecallQuestionId: sense.formRecallQuestionId },
          indexes.get(sense.formRecallQuestionId) ?? '',
          translationLanguage,
        ),
      )
    }
  }

  if (entry.kind === 'lexeme') {
    const forms = entryParadigm(entry)
    result.push(_paradigmTable(entry.id, forms))
    // Cells the class predicts are computable, so they earn no card (spec: residue).
    const residue = new Set(residueCells(entry, rule))
    for (const form of forms) {
      if (!residue.has(form.cell) || !form.formRecallQuestionId) continue
      result.push(
        ...formRecallParadigmCellBlock(
          { ...form, formRecallQuestionId: form.formRecallQuestionId },
          entrySenses(entry)[0],
          indexes.get(form.formRecallQuestionId) ?? '',
          entry.headword,
          translationLanguage,
        ),
      )
    }
  }
  return result
}

/** Shared mint-time projection used by adoption, edits, and learning selection. */
export function mintQuestionBlocks(entry: LexiconEntry, qid: string, kind: LexiconCardKind, l1: TranslationLanguage): Blocks {
  const children = entryBlocks(entry, qid, l1)
  const block = findBlock(children, candidate =>
    (candidate.type === 'question' || candidate.type === 'cloze') && candidate.qid === qid)
  if (!block) throw new Error(`Projected question block not found: ${qid}`)
  // The kind is the card row's column (card derivation reads it from the entry, question-card-triggers-ddl.ts), not a page value. Spoken cards default to a
  // 60 s TTA through the question's declared `tta` (flashcards-training.spec :224); others take the 30 s default.
  if (block.type === 'question' && (kind === 'dictation' || kind === 'readAloud')) block.tta = 60
  return children
}

export function hasQid(block: Block, qids: ReadonlySet<string>): boolean {
  return (('qid' in block && typeof block.qid === 'string' && qids.has(block.qid)) ||
    ('children' in block && Array.isArray(block.children) && block.children.some(child => hasQid(child, qids))))
}

export function entryBlocks(
  entry: LexiconEntry,
  questionId: string | undefined,
  translationLanguage?: TranslationLanguage,
): Blocks {
  if (!questionId) return []
  const kind = getLexiconCardType(entry, questionId)
  if (!kind) return []
  const index = _fractionalIndexes(entry).get(questionId) ?? ''

  if (kind === 'readAloud' && entry.kind === 'lexeme') {
    return [readAloudBlock(entry, index, translationLanguage)]
  }
  if (kind === 'dictation' && entry.kind === 'lexeme') {
    return [dictationBlock(entry, index, translationLanguage)]
  }
  if (kind === 'senseRecall') {
    const sense = entrySenses(entry).find(
      (candidate) => candidate.senseRecallQuestionId === questionId,
    )
    return sense?.senseRecallQuestionId
      ? [
          senseRecallBlock(
            { ...sense, senseRecallQuestionId: sense.senseRecallQuestionId },
            index,
            translationLanguage,
          ),
        ]
      : []
  }
  if (kind === 'formRecall') {
    const sense = entrySenses(entry).find(
      (candidate) => candidate.formRecallQuestionId === questionId,
    )
    return sense?.formRecallQuestionId
      ? formRecallBlock(
          { ...sense, formRecallQuestionId: sense.formRecallQuestionId },
          index,
          translationLanguage,
        )
      : []
  }
  if (entry.kind === 'lexeme') {
    const form = entryParadigm(entry).find(
      (candidate) => candidate.formRecallQuestionId === questionId,
    )
    return form?.formRecallQuestionId
      ? formRecallParadigmCellBlock(
          { ...form, formRecallQuestionId: form.formRecallQuestionId },
          entrySenses(entry)[0],
          index,
          entry.headword,
          translationLanguage,
        )
      : []
  }
  return []
}

function _fractionalIndexes(entry: LexiconEntry): Map<string, string> {
  const qids: string[] = []
  if (entry.kind === 'lexeme') {
    qids.push(entry.readAloudQuestionId, entry.dictationQuestionId)
  }
  for (const sense of entrySenses(entry)) {
    if (sense.senseRecallQuestionId) qids.push(sense.senseRecallQuestionId)
    if (sense.formRecallQuestionId) qids.push(sense.formRecallQuestionId)
  }
  if (entry.kind === 'lexeme') {
    qids.push(
      ...entryParadigm(entry).flatMap((form) =>
        form.formRecallQuestionId ? [form.formRecallQuestionId] : [],
      ),
    )
  }

  const result = new Map<string, string>()
  let previous = ''
  for (const qid of qids) {
    previous = getFractionalIndex(previous)
    result.set(qid, previous)
  }
  return result
}

export function clozeParagraph(
  qid: string,
  example: Example,
  fractionalIndex: string,
  imageSrc: string | undefined,
  cues: string[] = [],
  answerAudioSrc?: string,
  weight?: { importance: 'low'; difficulty: 'low' },
): Block {
  const [offset, length] = example.node
  const children: Blocks = []
  // The trainer renders ONLY the block carrying the qid
  // (`getTrainingExcerptBlocks`), so a cloze card's prompt has to live inside
  // this paragraph. As siblings these were silently dropped and the learner saw
  // a picture and a gap with nothing telling them which word to produce.
  // Plain text, NOT `em`: em paints a highlight box, which is reserved for the
  // target word. Three stacked highlighted lines read as damage, and it made
  // the highlight stop meaning anything.
  cues.forEach((cue, index) => {
    if (!cue) return
    children.push(
      { id: `${qid}-cue-${index}`, type: 'text', text: cue },
      { id: `${qid}-cue-${index}-br`, type: 'br' },
    )
  })
  if (imageSrc) children.push({ id: `${qid}-img`, type: 'img', src: imageSrc })
  children.push(
    { id: `${qid}-before`, type: 'text', text: example.text.slice(0, offset) },
    {
      id: qid,
      type: 'cloze',
      qid,
      fractionalIndex,
      ...(answerAudioSrc ? { answerAudio: { src: answerAudioSrc } } : {}),
      ...weight,
      children: [
        {
          id: `${qid}-answer`,
          type: 'text',
          text: example.text.slice(offset, offset + length),
        },
      ],
    },
    {
      id: `${qid}-after`,
      type: 'text',
      text: example.text.slice(offset + length),
    },
  )
  return { id: `${qid}-p`, type: 'p', children }
}

/**
 * The picture is on EVERY card, not only the cloze ones.
 *
 * It is the form-meaning link the whole entry is built around: a card that
 * withholds it is a weaker card, and a learner flicking between cards of one
 * word should see the same image each time rather than have it appear and
 * vanish. Word forms reuse the sense's image (spec: word-forms-reuse-the-
 * senses-image), so there is exactly one to show.
 */
function _imageBlock(qid: string, entry: LexiconEntry): Blocks {
  const [firstSense] = entrySenses(entry)
  return firstSense ? _senseImageBlock(qid, firstSense) : []
}

function _senseImageBlock(qid: string, sense: Sense): Blocks {
  const src = sense.example.imageSrc
  return src ? [{ id: `${qid}-img`, type: 'img', src }] : []
}

/** unni's task pill: every card says what the learner must do. */
function _taskLabel(id: string, label: string): Block {
  return {
    id,
    type: 'p',
    children: [
      {
        id: `${id}-em`,
        type: 'em',
        color: 'secondary',
        children: [{ id: `${id}-text`, type: 'text', text: label }],
      },
    ],
  }
}

/** unni's WordGradient: the target word is highlighted, not merely present. */
function _highlightedWord(id: string, word: string): Block {
  return {
    id,
    type: 'p',
    children: [
      {
        id: `${id}-em`,
        type: 'em',
        color: 'primary',
        children: [{ id: `${id}-text`, type: 'text', text: word }],
      },
    ],
  }
}

/** The example with its node highlighted in place, the word left visible. */
function _highlightedExample(qid: string, example: Example): Block {
  const [offset, length] = example.node
  return {
    id: `${qid}-p`,
    type: 'p',
    children: [
      {
        id: `${qid}-before`,
        type: 'text',
        text: example.text.slice(0, offset),
      },
      {
        id: `${qid}-em`,
        type: 'em',
        color: 'primary',
        children: [
          {
            id: `${qid}-node`,
            type: 'text',
            text: example.text.slice(offset, offset + length),
          },
        ],
      },
      {
        id: `${qid}-after`,
        type: 'text',
        text: example.text.slice(offset + length),
      },
    ],
  }
}

function _paragraph(id: string, text: string): Block {
  return {
    id: id.endsWith('-p') ? id : `${id}-p`,
    type: 'p',
    children: [{ id: `${id}-text`, type: 'text', text }],
  }
}

function _heading(id: string, text: string): Block {
  return {
    id,
    type: 'h',
    level: 2,
    children: [{ id: `${id}-text`, type: 'text', text }],
  }
}

function _paradigmTable(
  entryId: string,
  paradigm: readonly {
    cell: string
    form: string
    transcription: string
  }[],
): Block {
  return {
    id: `${entryId}-table`,
    type: 'table',
    children: paradigm.map((form, rowIndex) => ({
      id: `${entryId}-tr-${rowIndex}`,
      type: 'tr',
      children: [form.cell, form.form, form.transcription].map(
        (text, cellIndex) => ({
          id: `${entryId}-td-${rowIndex}-${cellIndex}`,
          type: 'td',
          children: [
            {
              id: `${entryId}-td-${rowIndex}-${cellIndex}-text`,
              type: 'text',
              text,
            },
          ],
        }),
      ),
    })),
  }
}
